import { Spinner } from '@nextui-org/react';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { KpiCard } from '../ui';
import type { PulseData } from '../../utils/industry-volume/build-pulse';
import { pulseKpis } from '../../utils/industry-volume/build-pulse';
import { day, pct, pts, seasonShort, signedPct, week } from './format';
import { useVolumeUnit } from './unit';

export type PulseBasis = 'etd' | 'eta';

/** Los indicadores: la industria a la izquierda y la exportadora a la derecha. */
export default function PulseSummary({ data, loading }: { data: PulseData; loading: boolean }) {
  const { t } = useTranslation('volume');
  const { v, label } = useVolumeUnit();
  const k = pulseKpis(data);
  const prev = seasonShort(data.prev_season);
  const w = week(data.cutoff_week);
  const u = t(label);
  // Los textos siguen a los datos que se muestran, no a la pestaña: mientras
  // carga la otra base, no se mezclan títulos nuevos con números viejos.
  const byArrival = data.basis === 'eta';
  const lines = (...items: (string | false | null | undefined)[]) => (
    <span className='flex flex-col'>
      {items.filter(Boolean).map((item) => (
        <span key={item as string}>{item}</span>
      ))}
    </span>
  );

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
        {loading ? (
          <Spinner size='sm' aria-label={t('Cargando…')} />
        ) : (
          <span className='text-xs text-default-500'>
            <span className='font-semibold'>{t('Base de comparación')}: </span>
            {t('temporada {{prev}} a la misma semana (zarpes hasta el {{prevDate}}).', {
              prev: data.prev_season,
              prevDate: day(data.prev_cutoff_etd),
            })}
          </span>
        )}
      </div>

      <div className={`grid grid-cols-1 gap-4 transition-opacity md:grid-cols-2 ${loading ? 'opacity-50' : ''}`}>
        {/* Izquierda la industria; derecha, lo mismo para la exportadora. */}
        <div className='flex flex-col gap-4'>
          <KpiCard
            label={
              byArrival
                ? t('Industria · arribado a la semana {{week}}', { week: w })
                : t('Industria · exportado a la semana {{week}}', { week: w })
            }
            value={`${v(k.industryCur)} ${u}`}
            detail={lines(
              t('{{growth}} vs {{prev}} a la misma semana', { growth: signedPct(k.industryGrowth), prev }),
              t('{{progress}} del total de {{prev}} ({{total}} {{unit}})', {
                progress: pct(k.progressOfPrevClose, 0),
                prev,
                total: v(k.prevTotal),
                unit: u,
              })
            )}
          />
          <KpiCard
            label={t('Industria · arribado al {{date}}', { date: day(data.last_etd) })}
            value={`${v(k.arrived)} ${u}`}
            detail={lines(
              t('{{share}} de lo exportado', { share: pct(k.arrivedShare) }),
              t('Por arribar: {{pending}} {{unit}} ({{share}})', {
                pending: v(k.pending),
                unit: u,
                share: pct(k.pendingShare),
              })
            )}
          />
        </div>
        <div className='flex flex-col gap-4'>
          <KpiCard
            label={
              byArrival
                ? t('{{name}} · arribado a la semana {{week}}', { name: data.mine_name ?? t('Tu exportadora'), week: w })
                : t('{{name}} · exportado a la semana {{week}}', { name: data.mine_name ?? t('Tu exportadora'), week: w })
            }
            value={k.hasMine ? `${v(k.mineCur)} ${u}` : '—'}
            detail={
              k.hasMine
                ? lines(
                    t('{{growth}} vs {{prev}} a la misma semana', { growth: signedPct(k.mineGrowth), prev }),
                    t('{{progress}} del total de {{prev}} ({{total}} {{unit}})', {
                      progress: pct(k.mineProgressOfPrevClose, 0),
                      prev,
                      total: v(k.minePrevTotal),
                      unit: u,
                    }),
                    k.rank.position != null &&
                      t('Ranking {{position}}° de {{count}} · market share {{share}} ({{delta}} pts)', {
                        position: k.rank.position,
                        count: k.rank.of,
                        share: pct(k.shareCur),
                        delta: pts(k.shareDeltaPts),
                      })
                  )
                : t('Tu exportadora todavía no está vinculada en la base de volumen.')
            }
          />
          <KpiCard
            label={t('{{name}} · arribado al {{date}}', {
              name: data.mine_name ?? t('Tu exportadora'),
              date: day(data.last_etd),
            })}
            value={k.hasMine ? `${v(k.mineArrived)} ${u}` : '—'}
            detail={
              k.hasMine
                ? lines(
                    t('{{share}} de lo exportado', { share: pct(k.mineArrivedShare) }),
                    t('Por arribar: {{pending}} {{unit}} ({{share}})', {
                      pending: v(k.minePending),
                      unit: u,
                      share: pct(k.minePendingShare),
                    })
                  )
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}
